/* eslint-disable max-len */
import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import { Container, useScreenClass } from "react-grid-system";
import styled from "styled-components";

import theme from "../styles/theme";
import { UserContext } from "../context/UserContext";
import Tree from "../components/Tree";
import { Col, Row } from "../components/StyledGridSystem";
import FlexBox, { FlexGrow } from "../components/FlexBox";
import { PillarType } from "../types/Roadmap";
import axios from "axios";
import { SemesterContext } from "../context/SemesterContext";
import { handleJWTError } from "../hooks/jwtErrHandler";
import { hasNoCurriculumVersionsForExplore } from "../utils/exploreCompetency";

type PageAreaProps = {
  signedIn: boolean;
  isSidebarOpen?: boolean;
  shouldShift?: boolean;
  isBehalfActive?: boolean;
} & React.HTMLProps<HTMLDivElement>;
const PageArea = styled.div<PageAreaProps>`
  padding-top: ${({ signedIn, theme }) => (signedIn ? 0 : 0)};
  padding-left: ${({ signedIn, isSidebarOpen, shouldShift, theme }) =>
    signedIn && isSidebarOpen && shouldShift ? 0 : 0};
  transition: all ${({ theme }) => theme.transitionTime.default};
`;

const StyledContainer = styled(Container)<{ $signedIn: boolean }>`
  padding-top: ${({ $signedIn, theme }) => ($signedIn ? 0 : 0)};
  padding-left: ${({ $signedIn }) => !$signedIn && 0} !important;
  padding-right: ${({ $signedIn }) => !$signedIn && 0} !important;
  max-width: ${({ $signedIn }) => !$signedIn && "100% !important"};
  max-width: 100% !important;
  color: #fff;
`;

const Droplist = styled.div`
  display: flex;
  width: 100%;
  flex-flow: column nowrap;
  margin-bottom: 0px;
  text-overflow: clip;
  white-space: nowrap;
`;

const LogoDiv = styled.div`
  display: flex;
  flex-flow: row;
`;

const Name = styled.div`
  position: absolute;
  left: 10%;
  right: 64.58%;
  top: 4.44%;
  bottom: 92.89%;

  font-family: system-ui;
  font-style: normal;
  font-weight: 800;
  font-size: 24px;
  line-height: 24px;
  /* identical to box height, or 100% */

  color: #ffffff;
`;

const EnrollStyled = styled.div`
  padding-top: ${({ theme }) => theme.spacing.xxs};
  color: ${({ theme }) => theme.color.textSecondary};
`;

export type Props = {
  children: React.ReactNode;
};

function SecondLayout({ children }: Props): JSX.Element {
  const screenClass = useScreenClass();
  // const currentUser = useContext(UserContext).user;
  const isMobile = useMemo(
    () => ["xs", "sm", "md"].includes(screenClass),
    [screenClass]
  );

  const shouldShowSidebarByDefault = useMemo(() => !isMobile, [isMobile]);
  const [isSidebarOpen, setSidebarOpen] = useState(shouldShowSidebarByDefault);
  const [isCourseOpen, setIsCourseOpen] = useState(false);
  const [isDocumentOpen, setIsDocumentOpen] = useState(false);
  const { isSignedIn, searchView } = useContext(UserContext);
  const [pillars, setPillars] = useState<PillarType>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const currentUser = useContext(UserContext).user;
  const semesterContext = useContext(SemesterContext);
  const { user } = useContext(UserContext);
  const latestFetchIdRef = useRef(0);

  const showNoVersionState = hasNoCurriculumVersionsForExplore(
    user.role,
    semesterContext.selectedSemester,
    semesterContext.semesterVersions,
    semesterContext.semesterVersionsLoading
  );

  const hideExploreTree =
    user.role !== 1 &&
    semesterContext.selectedSemester?.label &&
    semesterContext.selectedSemester.label !== "All" &&
    (showNoVersionState || semesterContext.semesterVersionsLoading);

  const toggleCourseDropdown = () => {
    setIsCourseOpen((prevState) => !prevState);
    if (isDocumentOpen) {
      setIsDocumentOpen(false);
    }
  };

  const toggleDocumentDropdown = () => {
    setIsDocumentOpen((prevState) => !prevState);
    if (setIsCourseOpen) {
      setIsCourseOpen(false);
    }
  };
  useEffect(() => {
    if (showNoVersionState) {
      ++latestFetchIdRef.current;
      setPillars([]);
      setLoading(false);
      return;
    }
    fetchCompetencies();
  }, [
    showNoVersionState,
    semesterContext.selectedSemester?.label,
    semesterContext.selectedSemesterVersion,
    semesterContext.semesterVersions,
    user.role,
    semesterContext.semesterVersionsLoading,
  ]);

  const fetchCompetencies = () => {
    const storedRole = localStorage.getItem("role");
    const initialRole = JSON.parse(storedRole as string);

    if (showNoVersionState) {
      setPillars([]);
      setLoading(false);
      return;
    }

    const requestId = ++latestFetchIdRef.current;

    setLoading(true);
    user.role !== 1
      ? axios
          .get(`/version?university_code=CMKL`)
          .then((res) => {
            const version_number = res.data.versions.find(
              (v: any) => v.status === "Active Prime"
            ).version_number;
            axios
              .get(
                `/subdomain?curriculum_version=${
                  semesterContext?.selectedSemesterVersion === 0 ? version_number : semesterContext?.selectedSemesterVersion
                }`
              )
              .then((res) => {
                if (requestId !== latestFetchIdRef.current) return;
                filterData(res.data.pillars);
                setLoading(false);
              })
              .catch((err) => {
                handleJWTError(err);
                setLoading(false);
              });
          })
          .catch((err) => {
            handleJWTError(err);
          })
      : semesterContext.selectedSemester.label === "All" || user.role !== 1
      ? axios
          .get(`/subdomain`)
          .then((res) => {
            if (requestId !== latestFetchIdRef.current) return;
            filterData(res.data.pillars);
          })
          .catch((err) => {
            handleJWTError(err);
            setLoading(false);
            setPillars([]);
          })
      : semesterContext.selectedSemester?.label &&
        axios
          .get(
            `/student/version?student_id=${initialRole.role_id}&semester_name=${semesterContext.selectedSemester.label}&program_id=${user.programs[0].id}`
          )
          .then((res) => {
            axios
              .get(
                `/subdomain?curriculum_version=${res.data.version.curriculum_version}`
              )
              .then((res) => {
                if (requestId !== latestFetchIdRef.current) return;
                filterData(res.data.pillars);
              })
              .catch((err) => {
                handleJWTError(err);
              });
          })
          .catch((err) => {
            handleJWTError(err);
            setLoading(false);
            setPillars([]);
          });
  };
  const filterData = (data: any) => {
    if (
      hasNoCurriculumVersionsForExplore(
        user.role,
        semesterContext.selectedSemester,
        semesterContext.semesterVersions,
        semesterContext.semesterVersionsLoading
      )
    ) {
      setPillars([]);
      setLoading(false);
      return;
    }

    const custom: PillarType = [];
    data.forEach((element: any, index: any) => {
      custom.push({
        id: element.id,
        node: index + 1,
        label: element.title,
        description: element.description,
        iconBg: element.graphics.iconbg,
        borderColor: element.graphics.bordercolor,
        icon: element.graphics.icon,
        iconShadow: element.graphics.iconshadow,
        children: [],
      });
      if (element.subdomains && element.subdomains.length > 0) {
        element.subdomains.forEach((elementChild: any, indexChild: any) => {
          custom[index].children.push({
            id: elementChild.id,
            node: Number(
              String(data.length) + String(index + 1) + String(indexChild + 1)
            ),
            label: elementChild.title,
            count: "0/7",
            icon: "",
            description: elementChild.description,
          });
        });
      }
    });
    setPillars(custom);
    setLoading(false);
  };

  // useEffect(() => () => {
  //   // Reset Sidebar when close
  //   if (isCourseOpen) {
  //     setIsCourseOpen(false);
  //   }
  //   if (isDocumentOpen) {
  //     setIsDocumentOpen(false);
  //   }
  // }, [isSidebarOpen]);

  useEffect(() => {
    setSidebarOpen(shouldShowSidebarByDefault);
  }, [shouldShowSidebarByDefault]);

  return (
    <>
      <FlexBox
        style={{
          flexWrap: "nowrap",
          userSelect: "none",
        }}
        alignItems="flex-start"
      >
        {!searchView && !hideExploreTree && <Tree data={pillars} />}
        <FlexGrow>
          <PageArea
            signedIn={isSignedIn}
            isSidebarOpen={isSidebarOpen}
            shouldShift={shouldShowSidebarByDefault}
          >
            <StyledContainer $signedIn={isSignedIn}>{children}</StyledContainer>
          </PageArea>
        </FlexGrow>
      </FlexBox>
    </>
  );
}

export default SecondLayout;
